import { StanModal } from '@/components';
import { useDeleteEmail, useFlowQuery } from '../queries';
import { stanNotify } from '@/lib';
import { useStanRouter } from '@/hooks/useStanRouter';

export interface DeleteEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    flowId: number;
    emailId: number;
}

export const DeleteEmailModal = ({ isOpen, onClose, flowId, emailId }: DeleteEmailModalProps) => {
    const { mutateAsync: deleteEmailMutation } = useDeleteEmail(flowId, emailId);
    const { data: flowData } = useFlowQuery({ flowId });
    const { push } = useStanRouter();

    const handleDelete = async () => {
        try {
            await deleteEmailMutation();
            stanNotify({
                title: 'Email deleted successfully',
                type: 'success',
            });
            onClose();

            // Determine where to navigate after deletion
            const flowEmails = flowData?.flow_emails || [];
            const remainingEmails = flowEmails.filter(email => email.flow_email_id !== emailId);

            if (remainingEmails.length > 0) {
                // Navigate to the first remaining email
                const firstEmailId = (remainingEmails[0] && remainingEmails[0].flow_email_id) || '';

                if (firstEmailId) {
                    push(`/flows/${flowId}/emails/${firstEmailId}`, { skipDirtyCheck: true });
                } else {
                    push(`/flows/${flowId}/new`, { skipDirtyCheck: true });
                }
            } else {
                // No emails left, navigate to new email page
                const trigger = flowData?.trigger || flowData?.trigger_type || 'checkout';
                push(`/flows/${flowId}/new?triggerType=${trigger}`, { skipDirtyCheck: true });
            }
        } catch (error) {
            const message = (error as any).response?.data?.message || 'Failed to delete email';
            stanNotify({
                title: message,
                type: 'error',
            });
        }
    };

    return (
        <StanModal
            id='delete-email-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Are you sure?'
            size='sm'
            primaryActionLabel='Delete Email'
            secondaryActionLabel='Cancel'
            primaryActionColor='destructive'
            secondaryActionColor='secondary'
            onPrimaryAction={handleDelete}
            onSecondaryAction={onClose}
            hideCloseButton
        >
            <p className='text-dark text-caption-1-regular text-center'>
                Just confirming that you want to delete this email. This action cannot be undone.
            </p>
        </StanModal>
    );
};
